/**
 * StructuralNode — Constraint graph node renderer.
 *
 * - Dimensions: circle with label
 * - Everything else: box with label + config params
 *
 * Each node has a 7px perimeter ring (the Handle zone).
 * Drag the ring to create connections; drag the inner shape to move the node.
 * FloatingEdge computes intersection geometry.
 */

import { memo, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { CONSTRAINT_COLOR } from '@/ui/shared/utils';
import { EditableLabel } from './EditableLabel';
import type { GraphNodeData } from './GraphNode';

const HANDLE_PAD = 7;
const RING_COLOR = 'rgba(255,255,255,0.07)';
const RING_COLOR_HOVER = 'rgba(255,255,255,0.15)';

const deleteButtonStyle: React.CSSProperties = {
  position: 'absolute',
  width: 20, height: 20, borderRadius: '50%',
  background: '#374151', border: 'none', color: '#ef4444',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, lineHeight: 1, padding: 0,
  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
  zIndex: 10,
};

// ─── Config param input ─────────────────────────────────────

function ConfigInput({ instanceId, nodeId, paramId }: {
  instanceId: string; nodeId: string; paramId: string;
}) {
  const { getValue, tweakValue } = useRuntime();
  const value = getValue(instanceId, nodeId, paramId) as number ?? 0;
  const [text, setText] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync from outside when not focused
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(String(value));
    }
  }, [value]);

  const commit = useCallback(() => {
    const v = parseFloat(text);
    if (!isNaN(v)) tweakValue(instanceId, nodeId, paramId, v);
    else setText(String(value));
  }, [text, value, instanceId, nodeId, paramId, tweakValue]);

  return (
    <input
      ref={inputRef}
      type="text"
      className="nodrag nopan nowheel"
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { commit(); inputRef.current?.blur(); } }}
      style={{
        width: 52, background: '#111827', border: '1px solid #374151',
        borderRadius: 4, color: 'white', fontSize: 11, padding: '2px 4px',
        textAlign: 'right', outline: 'none',
      }}
    />
  );
}

// ─── Main component ──────────────────────────────────────────

function StructuralNodeComponent({ data, selected }: { data: GraphNodeData; id: string; selected?: boolean }) {
  const { instanceId, node } = data;
  const { deleteNode } = useRuntime();
  const isEntity = node.type === 'struct.Entity' || node.type === 'grid.Entity';
  const isConstraint = !isEntity;
  const [hovered, setHovered] = useState(false);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNode(instanceId, node.id);
  }, [instanceId, node.id, deleteNode]);

  const { configParams, constraintEndpoints } = useMemo(() => {
    const configs: { id: string; label: string }[] = [];
    const constraints: { id: string; isOutput: boolean; isInput: boolean }[] = [];

    for (const [epId, ep] of Object.entries(node.endpoints)) {
      if (ep.dataFlow === 'internal' || (ep.custom && (ep.isInput || ep.isOutput))) {
        // Internal endpoints (system dimension inputs, grid handle) or custom params (layers)
        constraints.push({ id: epId, isOutput: !!ep.isOutput, isInput: !!ep.isInput });
      } else if (ep.dataType === 'number' && ep.dataFlow === 'param') {
        configs.push({ id: epId, label: ep.label || epId });
      }
    }
    return { configParams: configs, constraintEndpoints: constraints };
  }, [node.endpoints]);

  const constraintBorder = CONSTRAINT_COLOR + '60'; // 38% opacity
  const borderColor = selected ? '#3b82f6' : isConstraint ? constraintBorder : '#374151';
  const textColor = isConstraint ? CONSTRAINT_COLOR : 'white';
  const ringBg = hovered ? RING_COLOR_HOVER : RING_COLOR;

  // ── Box ──
  const boxRadius = isConstraint ? 0 : 8;

  return (
    <div
      style={{ position: 'relative', padding: HANDLE_PAD }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Perimeter ring */}
      <div style={{
        position: 'absolute', inset: 0,
        borderRadius: boxRadius + HANDLE_PAD,
        background: ringBg,
        transition: 'background 0.15s',
      }} />

      {/* Inner box — dragging this moves the node */}
      <div style={{
        background: '#1f2937',
        border: `1px solid ${borderColor}`,
        borderRadius: boxRadius,
        minWidth: 120,
        color: 'white',
        fontSize: 12,
        position: 'relative', zIndex: 1,
        boxShadow: selected ? '0 0 12px rgba(59, 130, 246, 0.4)' : undefined,
        pointerEvents: 'auto',
      }}>
        <div style={{ padding: '6px 10px', borderBottom: configParams.length > 0 ? '1px solid #374151' : 'none' }}>
          <EditableLabel nodeId={node.id} label={node.label} style={{ color: textColor, fontSize: 12 }} />
        </div>

        {/* Config params */}
        {configParams.length > 0 && (
          <div style={{ padding: '4px 10px 8px' }}>
            {configParams.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, height: 24 }}>
                <span style={{ color: textColor, fontSize: 11 }}>{p.label}</span>
                <ConfigInput instanceId={instanceId} nodeId={node.id} paramId={p.id} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete button — top right */}
      {hovered && (
        <button
          onClick={handleDelete}
          className="nodrag nopan"
          style={{ ...deleteButtonStyle, top: 0, right: 0 }}
          title="Delete node"
        >✕</button>
      )}

      {/* Handles cover full outer area (including ring) */}
      {constraintEndpoints.map(ep => (
        <Handle
          key={ep.id}
          type={ep.isOutput ? 'source' : 'target'}
          position={Position.Top}
          id={ep.id}
          className="nodrag"
          style={{
            width: '100%', height: '100%',
            background: 'transparent', border: 'none', borderRadius: 0,
            top: 0, left: 0, transform: 'none',
          }}
        />
      ))}
    </div>
  );
}

export const StructuralNode = memo(StructuralNodeComponent);
